import { triggerAction } from "../../constants"
import { loadBalancesStart } from "../../actions/getVslaBalance"
import { fetchMeetingMemberTransactions } from "../../actions/getTransactions"
import uuid from "uuid"
import { backendAPI, transactionAPI } from "../../api"
import {
  db,
  decryptObject,
  updateDependencyRecords,
} from "../../../view/shared/utilities/cache"
import { safeApiCall } from "../../api/offline"

// --- Default State Structures ---
const defaultGroupAccounts = () => ({
  balances: {
    loan: { assetType: "SC", balance: 0, accountType: "loan" },
    loan_interest: { assetType: "SC", balance: 0, accountType: "loan_interest" },
    welfare: { assetType: "SC", balance: 0, accountType: "welfare" },
    savings: { assetType: "SC", balance: 0, accountType: "savings" },
    shares: { assetType: "SHAR", balance: 0, accountType: "shares" },
    "fine paid": { assetType: "SC", balance: 0, accountType: "fine paid" },
    membership: { assetType: "SC", balance: 0, accountType: "membership" },
    agri: { assetType: "SC", balance: 0, accountType: "agri" },
  },
  members: {},
  transactions: {},
})

const defaultMemberAccounts = () => ({
  loan: { assetType: "SC", balance: 0, accountType: "loan" },
  total_loan_interest: {
    assetType: "SC",
    balance: 0,
    accountType: "total_loan_interest",
  },
  paid_loan_interest: {
    assetType: "SC",
    balance: 0,
    accountType: "paid_loan_interest",
  },
  loan_interest: {
    assetType: "SC",
    balance: 0,
    accountType: "loan_interest",
  },
  welfare: { assetType: "SC", balance: 0, accountType: "welfare" },
  welfare_requests: {
    assetType: "SC",
    balance: 0,
    accountType: "welfare_requests",
  },
  savings: { assetType: "SC", balance: 0, accountType: "savings" },
  shares: { assetType: "SC", balance: 0, accountType: "shares" },
  "Fine paid": { assetType: "SC", balance: 0, accountType: "Fine paid" },
  membership: { assetType: "SC", balance: 0, accountType: "membership" },
  agri: { assetType: "SC", balance: 0, accountType: "agri" },
})

export const applyTransaction = (state, payload) => {
  const { type, amount, member_id } = payload
  const newState = JSON.parse(JSON.stringify(state)) // Deep copy to avoid mutations

  if (member_id && !newState.members[member_id]) {
    newState.members[member_id] = defaultMemberAccounts()
  }

  const updateGroupBalance = (accountType, factor) => {
    if (newState.balances[accountType]) {
      newState.balances[accountType].balance += amount * factor
    }
  }

  const updateMemberBalance = (accountType, factor) => {
    if (
      member_id &&
      newState.members[member_id] &&
      newState.members[member_id][accountType]
    ) {
      newState.members[member_id][accountType].balance += amount * factor

    }
  }

  switch (type) {
    // --- Income Transactions ---
    case "BUY_SHARES":
      updateGroupBalance("savings", 1) // Group receives cash
      updateMemberBalance("shares", 1) // Member gets shares
      break
    case "LOAN_REPAYMENT":
      updateGroupBalance("savings", 1) // Group receives cash
      updateMemberBalance("loan", -1) // Member's loan decreases
      break
    case "MEMBERSHIP_FEE":
      updateGroupBalance("membership", 1)
      updateMemberBalance("membership", 1)
      break
    case "FINE_PAYMENT":
      updateGroupBalance("Fine paid", 1)
      updateMemberBalance("Fine paid", 1)
      break
    case "LOAN_INTEREST_REPAYMENT":
      updateGroupBalance("loan_interest", 1)
      updateMemberBalance("loan_interest", 1)
      break
    case "WELFARE_CONTRIBUTION":
      updateGroupBalance("welfare", 1)
      updateMemberBalance("welfare", 1)
      break
    case "FUND_AGRI":
      updateGroupBalance("agri", 1)
      updateMemberBalance("agri", 1)
      break
    case "GROUP_INCOME":
      updateGroupBalance("savings", 1)
      break

    // --- Expense Transactions ---
    case "SELL_SHARES":
      updateGroupBalance("savings", -1) // Group gives cash
      updateMemberBalance("shares", -1) // Member loses shares
      break
    case "CREATE_LOAN":
      updateGroupBalance("savings", -1) // Group gives cash
      updateMemberBalance("loan", 1) // Member's loan increases
      break
    case "WELFARE_REQUEST":
      updateGroupBalance("welfare", -1)
      updateMemberBalance("welfare_requests", 1)
      break
    case "WRITE_OFF_LOAN":
      updateGroupBalance("savings", -1) // Writing off a loan is a loss for the group's main fund
      updateMemberBalance("loan", -1)
      break
    case "GROUP_EXPENSE":
      updateGroupBalance("savings", -1)
      break

    default:
      break
  }

  return newState
}

export const hydrateOfflineBalance = () => async (dispatch, getState) => {
  const { user, accountV2, loadedvslaMembers } = getState()
  if (!user?.id) return
  try {
    let pendingRequests = await db
      .table("offlineRequests")
      .where("[userId+status]")
      .equals([user.id, "pending"])
      .toArray()

    const erroredRequests = await db
      .table("offlineRequests")
      .where("[userId+status]")
      .equals([user.id, "error"])
      .toArray()

    pendingRequests = pendingRequests.filter(
      (req) =>
        req.entityType && [INCOME_TRANSACTION, EXPENSE_TRANSACTION].flat().includes
          (req.entityType)
    )

    if (pendingRequests.length === 0 && erroredRequests.length === 0) {
      // clear offline balance state
      dispatch({ type: "CLEAR_OFFLINE_BALANCE" })
      return
    }
    // If online state is empty, start from a default structure.
    const startingState =
      Object.keys(accountV2.online.balances).length > 0
        ? accountV2.online
        : defaultGroupAccounts()

    let calculatedState = JSON.parse(JSON.stringify(startingState))

    for (const request of pendingRequests) {
      try {
        const decryptedString = decryptObject(request.encryptedBody, request.salt)
        const decryptedBody = JSON.parse(decryptedString)
        let memberPubkey = decryptedBody?.needBodyUpdate?.member_id;
        const memberId = decryptedBody?.body?.data?.member_id

        if (memberId) {
          // load the pubkey of the user
          memberPubkey = loadedvslaMembers.find(
            (m) => m.id === memberId,
          )?.pubkey
        }

        if (memberPubkey) {
          // Ensure we use the consistent pubkey (uid) as the memberId for hydration
          const hydrationPayload = {
            ...decryptedBody.body.data,
            member_id: memberPubkey, // this should be the pubkey
          }
          calculatedState = applyTransaction(calculatedState, hydrationPayload)
        }
      } catch (e) {
        console.error(
          `[Hydration] Failed to decrypt request ${request.cacheId}. Marking as failed.`,
          e,
        )
        await db
          .table("offlineRequests")
          .update(request.cacheId, { status: "decryption_failed" })
        continue // Skip to the next request
      }
    }

    dispatch({
      type: "INITIALIZE_OFFLINE_STATE",
      payload: calculatedState,
    })
  } catch (error) {
    console.error("Error hydrating offline balance:", error)
  }
}

export const reverseTransaction =
  (data) => async (dispatch, getState) => {
    const { user, loadedMember, group, meeting } =
      getState()
    if (group?.is_cashout_active) {
      alert("Cash out is in progress, can not processed")
      return
    }
    const meetingId = meeting?.ongoing.id
    const requestId = uuid.v4()
    delete data.uid
    const url = `${window.REACT_APP_PWA_APP_DOMAIN}/transactions/reverse?request_id=${requestId}`

    const requestObject = {
      type: "WEBSOCKET_ACTION",
      pushEvent: "requests",
      receiveEvent: "response",
      protocol: "ws",
      tempId: requestId,
      dependsOn: null,
      emit: true,
      metadata: {
        userId: user?.id,
        callBackParams: [loadedMember?.id, requestId],
        type: "transactions",
      },
      payload: {
        requestId,
        url,
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        userId: user?.id,
        method: "POST",
        data: {
          meeting_id: meetingId,
          transaction_id: data.id,
          reason: "Transaction Reversal",
        },
      },
    }
    return safeApiCall(requestObject)
  }

export const INCOME_TRANSACTION = [
  "BUY_SHARES",
  "MEMBERSHIP_FEE",
  "FINE_PAYMENT",
  "LOAN_INTEREST_REPAYMENT",
  "WELFARE_CONTRIBUTION",
  "FUND_AGRI",
  "GROUP_INCOME",
  "LOAN_REPAYMENT",
]

export const EXPENSE_TRANSACTION = [
  "SELL_SHARES",
  "CREATE_LOAN",
  "WELFARE_REQUEST",
  "WRITE_OFF_LOAN",
  "GROUP_EXPENSE",
]

export const initiateTransaction =
  (data, isRetry = false) =>
    async (dispatch, getState) => {
      const { user, group, meeting, loadedMember, loadedvslaMembers, accountV2 } =
        getState()
      if (group?.is_cashout_active) {
        alert("Cash out is in progress, can not processed")
        return
      }
      let payload = { ...data, group_id: group?.id }
      const tempId = data?.uuid || uuid.v4()
      let needBodyUpdate = null
      const metaObject = {
        userId: user?.id,
      }
      if (!isRetry) {
        const ongoing = meeting?.ongoing
        const meetingObject = ongoing?.id ? null : ongoing

        const memberCacheId = data?.member_id ? null : data?.memberCacheId
        const meetingCacheId = ongoing?.id ? null : meetingObject?.cacheId
        const dependencies = {}

        if (memberCacheId) {
          delete payload.memberCacheId
          dependencies["member_id"] = memberCacheId
          payload.member_id = null // It will be injected by the worker
          await updateDependencyRecords(
            [memberCacheId, metaObject?.userId],
            {
              ...metaObject,
              value: null,
              key: "member_id",
              entityType: memberCacheId,
            },
            metaObject.userId,
          )
        }
        // Ensure meetingId is part of the payload for the request body
        if (meetingCacheId) {
          payload.meeting_id = null // It will be injected by the worker
          dependencies["meeting_id"] = meetingCacheId
          await updateDependencyRecords(
            [meetingCacheId, metaObject?.userId],
            {
              ...metaObject,
              value: null,
              key: "meeting_id",
              entityType: meetingCacheId,
            },
            metaObject.userId,
          )
        } else {
          payload.meeting_id = ongoing?.id
        }

        if (!payload.group_id) {
          dependencies["group_id"] = "group"
          await updateDependencyRecords(
            ["group", metaObject?.userId],
            {
              ...metaObject,
              value: null,
              key: "group_id",
              entityType: "group",
            },
            metaObject.userId,
          )
        }

        needBodyUpdate = Object.keys(dependencies).length > 0 ? dependencies : null
        payload = {
          ...payload,
          request_id: tempId,
          transaction_type: payload.type,
          createdAt: new Date().toISOString(),
        }
      }
      try {
        // --- Offline Balance Logic ---
        if (!accountV2.offline) {
          // If online state is empty, initialize with a default structure.
          const initialState =
            Object.keys(accountV2.online.balances).length > 0
              ? accountV2.online
              : defaultGroupAccounts()

          dispatch({
            type: "INITIALIZE_OFFLINE_STATE",
            payload: initialState,
          })
        }
        // Use the pubkey from the currently loaded member as the consistent ID
        let member = loadedMember
        if (!member) {
          // the request has failed and the user clicked retry
          member = loadedvslaMembers.find((m) => m.id === data.member_id)
        }
        const offlineTxPayload = {
          ...data,
          member_id: member.pubkey,
        }
        dispatch({
          type: "UPDATE_OFFLINE_TRANSACTION",
          payload: offlineTxPayload,
        })
        // --- End Offline Balance Logic ---

        dispatch(
          triggerAction("ADD_TRANSACTION", {
            ...payload,
            isOffline: true,
            transactionPartyName: member.full_name,
            status: "pending",
            request_id: tempId,
            createdAt: new Date().toISOString(),
          }),
        )

        const requestObject = {
          type: "WEBSOCKET_ACTION",
          pushEvent: "requests",
          receiveEvent: "response",
          emit: true,
          protocol: "ws",
          dependsOn: null,
          tempId: tempId,
          entityType: payload.transaction_type,
          userId: user?.id,
          optimisticActions: [],
          needBodyUpdate: needBodyUpdate,
          metadata: {
            userId: user?.id,
            callBackParams: [payload.member_id, tempId],
            cacheKey: tempId,
            type: "transactions",
          },
          payload: {
            requestId: tempId,
            url: `${window.REACT_APP_PWA_APP_DOMAIN}/transactions/`,
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
            userId: user?.id,
            method: "POST",
            data: payload,
          },
        }
        return safeApiCall(requestObject)
      } catch (error) {
        const { response } = error
        if (!response || !response.data) {
          return
        }
        return Promise.reject()
      }
    }

export const getBalances = () => async (dispatch, getState) => {
  const id = getState().group?.id
  if (!id) {
    console.error("Group ID is missing.")
    return Promise.reject(new Error("Group ID is missing."))
  }
  try {
    const url = `/groups-hedera/${id}/balances/`
    dispatch(loadBalancesStart(true))
    const { data } = await safeApiCall(
      {
        cacheEnabled: true,
        url: url,
      },
      async () => backendAPI.get(url),
    )
    dispatch(triggerAction("SET_BALANCES", data))
    dispatch(hydrateOfflineBalance())
    dispatch(loadBalancesStart(false))
  } catch (error) {
    dispatch(loadBalancesStart(false))
    console.log("Error: ", error)
  }
}

export const getBalanceByType = (type) => async (dispatch, getState) => {
  const id = getState().group.blockchain_wallet_id
  if (!id) {
    console.error("Group blockchain key is missing.")
    return Promise.reject(new Error("Group blockchain key is missing."))
  }
  try {
    const url = `/${id}/type/${type}/balances`
    const { data } = await safeApiCall(
      {
        cacheEnabled: true,
        url: url,
      },
      async () => transactionAPI.get(url),
    )
    dispatch(
      triggerAction("SET_TYPE_BALANCES", {
        data,
        transactionType: type,
      }),
    )
  } catch (error) {
    console.log("Error: ", error)
  }
}

export const getMemberBalanceByMemberId =
  (memberId, cacheId) => async (dispatch, getState) => {
    const { group, loadedvslaMembers } = getState()
    const id = group?.id
    if (!id || !memberId) {
      return
    }
    try {
      const url = `/members/${memberId}/balances`
      const { data } = await safeApiCall(
        {
          cacheEnabled: true,
          url: url,
        },
        async () => backendAPI.get(url),
      )
      const memberPubkey = loadedvslaMembers.find((m) => m.id === memberId)?.pubkey

      if (!memberPubkey) {
        console.log("Member not found for balance update", memberId)
        return
      }
      await dispatch(fetchMeetingMemberTransactions(memberId))
      data.forEach((item) => {
        dispatch(
          triggerAction("SET_MEMBER_BALANCES", {
            data: item,
            memberId: memberPubkey, // use the pubkey as memberId
            transactionType: item.accountType,
          }),
        )
      })
      dispatch(getBalances())
      dispatch(hydrateOfflineBalance())
      return Promise.resolve()
    } catch (error) {
      console.log("Error: ", error)
    }
  }

export const getGroupMemberBalances = () => async (dispatch, getState) => {
  try {
    const { group, loadedvslaMembers } = getState()
    const id = group?.id
    if (!id) {
      console.error("Group ID is missing.")
      return Promise.reject(new Error("Group ID is missing."))
    }
    const { data } = await safeApiCall(
      {
        cacheEnabled: true,
        url: `/${id}/member-balances`,
      },
      async () => backendAPI.get(`/groups-hedera/${id}/member-balances`),
    )
    const membersAccounts = {}
    for (const memberId in data) {
      const memberPubkey = loadedvslaMembers.find((m) => m.id === memberId)?.pubkey
      const account = data[memberId]
      account.forEach(
        (item) =>
        (membersAccounts[memberPubkey] = {
          ...membersAccounts[memberPubkey],
          [item.accountType]: item,
        }),
      )
    }
    dispatch(triggerAction("SET_MEMBER_ACCOUNTS", membersAccounts))
  } catch (error) {
    console.log("error: ", error)
  }
}
